import { useState } from 'react'

import type { Locale } from '@skytech/types'
import { Dropzone, MessagePopup, UploadedFileZone } from '@skytech/ui'
import { getWhiteLabelForDealerStyle } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import TestTubeIcon from 'mdi-react/TestTubeIcon'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import documentResources from '@/api/document'
import queries from '@/api/queries'
import userResources from '@/api/user'
import { useAppContext } from '@/App.context'
import Page from '@/components/content/Page'
import Button from '@/components/form/Button'
import { useText } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import { DOCUMENT_TYPES } from '@/components/util/dropzone/constants'
import useAuthFilesDropzone from '@/components/util/dropzone/useAuthFilesDropzone'
import { setLocale as setReduxLocale } from '@/ducks/i18nReducer'
import { activatePopup } from '@/ducks/popupReducer'
import { csvExport } from '@/util/csv-export'

const csvTestData = [
  { name: 'test', value: 1 },
  { name: 'test', value: 2 },
]

const TestComponent = () => {
  const dispatch = useDispatch()

  const { whiteLabel, setLocale } = useAppContext()
  const [uploadedFileInfo, setUploadedFileInfo] = useState<{ id: number; filename: string } | null>(
    null
  )
  const [uploadError, setUploadError] = useState('')
  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState(false)

  const { refetch } = useQuery(queries.profile(), userResources.getProfile, { enabled: false })

  const derToasting = () => {
    toast('Show now')
  }

  const testMessage = useText('Oddy er {best}', { best: 'works like a charmio!' })

  const handleChangeLocale = (locale: Locale) => {
    setLocale(locale)
    // todo: remove as soon as all the dependencies are refactored
    setReduxLocale(locale)
  }

  const { authorizeUpload, isAuthorizingUpload } = useAuthFilesDropzone({
    // Magdalena's id
    customerId: 233032,
    // This endpoint is temporarily broken
    type: DOCUMENT_TYPES.TEST,
    fileTypes: ['pdf', 'msg', 'docx'],
    onUpload: res => {
      setUploadError('')
      setUploadedFileInfo(res)
    },
    onError: err => setUploadError(err),
  })

  const { refetch: authorizeFileDownload, isInitialLoading: isDownloading } = useQuery(
    uploadedFileInfo ? queries.documentDownloadAuthorization(uploadedFileInfo.id) : [''],
    () => {
      if (uploadedFileInfo) return documentResources.authorizeFileDownload(uploadedFileInfo.id)
    },
    {
      enabled: false,
      onError() {
        setIsErrorPopupOpen(true)
      },
    }
  )

  return (
    <Page>
      <h1>Test {testMessage}</h1>
      <div>
        <Button onClick={() => handleChangeLocale('en')}>en</Button>

        <Button onClick={() => handleChangeLocale('no')}>no</Button>

        <Button onClick={derToasting}>Toast</Button>
      </div>

      <div>
        <Button onClick={refetch} Icon={TestTubeIcon}>
          Get profile
        </Button>
      </div>

      {uploadedFileInfo ? (
        <UploadedFileZone
          documentFilename={uploadedFileInfo.filename}
          isDownloading={isDownloading}
          onDownloadClick={() => authorizeFileDownload()}
          onRemoveClick={() => setUploadedFileInfo(null)}
        />
      ) : (
        <Dropzone
          id="dropzone"
          activeDragText={<Translation id="Release file here to upload" />}
          inActiveDragText={<Translation id="Drag file here or click to browse" />}
          customError={uploadError}
          isUploading={isAuthorizingUpload}
          onDrop={authorizeUpload}
          whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
        />
      )}

      <MessagePopup
        title={<Translation id="File download error" />}
        message={
          <Translation id="We encountered a problem while downloading your file. Please refresh and try again." />
        }
        isOpen={isErrorPopupOpen}
        onClose={() => setIsErrorPopupOpen(false)}
        whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
      />

      <Button onClick={() => dispatch(activatePopup('This is a test'))}>Popup</Button>
      <Button onClick={() => csvExport(csvTestData, 'test.csv')}>CSV-test</Button>
    </Page>
  )
}

export default TestComponent
